import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import { useLocation } from 'react-router-dom';
import Canvas from './Canvas';
import { usePedigreeInstance } from './pedigree/instanceContext';
import { useWorkspace } from 'src/store/WorkspaceContext';
import { deriveProxyNodes } from './pedigree/deriveProxies';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Popper,
  Tooltip
} from '@mui/material';
import {
  AddCircle,
  Info,
  Keyboard,
  OpenWith,
  PeopleAlt,
  Redo,
  RemoveCircle,
  Undo
} from '@mui/icons-material';
import * as d3 from 'd3';
import ContextMenu from './canvas-tools/ContextMenu';
import EdgeContextMenu from './canvas-tools/EdgeContextMenu';
import { SIDEBAR_VALUE } from '../pedigree-sidebar/util';
import { AppContext, ThemeContext, UserContext } from 'src/store/ContextStore';
import axios from 'src/axios';
import { APICONSTANTS, PEDIGREE } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';
import PedigreeSideBar from '../pedigree-sidebar/PedigreeSideBar';
import LocatePerson from './canvas-tools/LocatePerson';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import CanvasPreferences from './canvas-tools/CanvasPreferences';
import ClearPosition from './canvas-tools/ClearPosition';
import AutoArrange from './canvas-tools/AutoArrange';
import {
  PedigreeSaveIndicator,
  ENABLE_STACKED_SAVE_INDICATOR
} from './canvas-tools/PedigreeSaveIndicator';
import { config as layoutConfig, isReproNode } from './pedigree/config';
import { getAllowedRelativeActions, RELATIVE_ACTIONS } from './pedigree/utils';
import {
  addSon,
  addDaughter,
  addUnknown,
  addSpouse,
  addBrother,
  addSister,
  addParents,
  addGrandparents
} from './canvas-tools/options/addRelativeActions';
import { stripMemberReferencesToPid } from './pedigree/edgeIntegrity';
import {
  resolveShortcut,
  isEditableTarget,
  PANELS_NEEDING_NODE
} from './keyboardShortcuts';
import ShortcutsHelp from './canvas-tools/ShortcutsHelp';
import LockBanner from './canvas-tools/LockBanner';
import BulkEditModal from '../BulkEditModal';
import MergePersonSelectModal from 'src/components/merge-person/MergePersonSelectModal';
import MergePersonWizard from 'src/components/merge-person/MergePersonWizard';
import MigrateModal from '../MigrateModal';
import DeleteNode from './canvas-tools/options/DeleteNode';
import {
  getEligiblePids,
  getComponentOf,
  getMigratablePidsDetail
} from './migrateUtil';
import { getProbandState } from './pedigree/probandState';
import { isAnonymousActive } from '../anonymousPedigrees';
import { useQueryClient } from '@tanstack/react-query';

const ChartBox = forwardRef(function ChartBox(
  {
    pedRef,
    user,
    memberRecords,
    canvasMembers,
    carrier = {},
    carrierStyle = {},
    setMemberRecords,
    setPedigreeState,
    labelsSet,
    setLabelsSet,
    options,
    setOptions,
    colors,
    patient,
    customPositions,
    setCustomPositions,
    establishedOrder,
    setEstablishedOrder,
    orderLocked = false,
    setOrderLocked = null,
    isLocked,
    isMine,
    lockState,
    requestStatus,
    onRequestLock,
    onCancelRequest,
    onForceAcquireLock,
    pendingRequest,
    onRespondToRequest,
    personLockHook,
    isMigrated,
    onSidebarChange,
    analysis,
    linkStatusMap = {},
    refetchLinkStatus,
    isSaving = false,
    onHistoryCheckpoint = null,
    onHistoryResolve = null,
    onHistoryUndo = null,
    onHistoryRedo = null,
    canUndo = false,
    canRedo = false,
    viewHide = null
  },
  ref
) {
  const { theme } = React.useContext(ThemeContext);
  const { user: loggedInUser } = React.useContext(UserContext);
  const { dispatchUpdate, content } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  // Merge wizard is name-driven (person search + PII details step) — no
  // entry point in anonymous mode; the backend strips the commit anyway.
  const anonymousPedigrees = isAnonymousActive(
    content?.settings?.pedigreeSetting
  );

  const [menu, setMenu] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const [edgeMenu, setEdgeMenu] = React.useState(null);
  const [edgeAnchorEl, setEdgeAnchorEl] = React.useState(null);

  const proxies = React.useMemo(
    () =>
      (memberRecords || []).flatMap((m) =>
        (Array.isArray(m.proxyIds) ? m.proxyIds : []).map((proxyId) => ({
          originalPid: m.pid,
          proxyId
        }))
      ),
    [memberRecords]
  );

  const canvasMembersWithProxies = React.useMemo(
    () => deriveProxyNodes(canvasMembers, proxies),
    [canvasMembers, proxies]
  );

  const handleAddProxy = React.useCallback(
    (intent) => {
      if (intent?.originalPid == null || intent?.proxyId == null) return;
      setMemberRecords((prev) =>
        (prev || []).map((m) => {
          if (m.pid !== intent.originalPid) return m;
          const existing = Array.isArray(m.proxyIds) ? m.proxyIds : [];
          if (existing.includes(intent.proxyId)) return m;
          return { ...m, proxyIds: [...existing, intent.proxyId] };
        })
      );
      setPedigreeState?.((s) => ({
        ...s,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    },
    [setMemberRecords, setPedigreeState]
  );

  const handleRemoveProxy = React.useCallback(
    (intent) => {
      if (intent?.originalPid == null || intent?.proxyId == null) return;
      setMemberRecords((prev) =>
        (prev || []).map((m) => {
          if (m.pid !== intent.originalPid) return m;
          const existing = Array.isArray(m.proxyIds) ? m.proxyIds : [];
          if (!existing.includes(intent.proxyId)) return m;
          return {
            ...m,
            proxyIds: existing.filter((id) => id !== intent.proxyId)
          };
        })
      );
      setPedigreeState?.((s) => ({
        ...s,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    },
    [setMemberRecords, setPedigreeState]
  );
  React.useEffect(() => {
    const records = memberRecords || [];
    const pidSet = new Set(records.map((m) => m.pid));
    let changed = false;
    const cleaned = records.map((m) => {
      if (!Array.isArray(m.proxyIds) || m.proxyIds.length === 0) return m;
      const valid = m.proxyIds.filter(
        (pid) => pidSet.has(pid) && (m.spouseIds || []).includes(pid)
      );
      if (valid.length === m.proxyIds.length) return m;
      changed = true;
      return { ...m, proxyIds: valid };
    });
    if (changed) {
      setMemberRecords(cleaned);
      setPedigreeState?.((s) => ({
        ...s,
        save: PEDIGREE.saveStatus.IN_PROGRESS
      }));
    }
  }, [memberRecords, setMemberRecords, setPedigreeState]);

  // Active-tab flag from the workspace host: global window keyboard shortcuts
  // (Ctrl+A select-all, arrow-nudge, undo/redo) must act only on the visible
  // chart, never on the other charts kept mounted behind it. Defaults to true for
  // a lone chart (no provider), so single-pedigree behaviour is unchanged.
  const { isActive, instanceId } = usePedigreeInstance();
  const workspace = useWorkspace();
  const wsUpdateViewState = workspace?.updateViewState;
  const isActiveRef = React.useRef(isActive);
  React.useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  // state for target node data
  const [data, setData] = useState(null);
  // state for target node
  const prevNode = React.useRef();

  const [sidebar, setSidebar] = useState({
    open: false,
    expand: false,
    expandAccordian: []
  });

  React.useEffect(() => {
    onSidebarChange?.({ open: sidebar.open, expand: sidebar.expand });
  }, [sidebar.open, sidebar.expand]); // eslint-disable-line react-hooks/exhaustive-deps

  const [error, setError] = React.useState(false);
  const [bulkEditOpen, setBulkEditOpen] = React.useState(false);

  const [shortcutsHelpOpen, setShortcutsHelpOpen] = useState(false);
  const [shortcutsEnabled, setShortcutsEnabled] = useState(
    () => localStorage.getItem('pedigree-shortcuts-enabled') !== 'false'
  );
  const toggleShortcutsEnabled = React.useCallback(() => {
    setShortcutsEnabled((prev) => {
      const next = !prev;
      localStorage.setItem('pedigree-shortcuts-enabled', String(next));
      return next;
    });
  }, []);

  const [selectionMode, setSelectionMode] = React.useState(false);
  const selectionModeRef = React.useRef(false);

  // Freehand / Move mode (Phase 3): an explicit opt-in toggle. When ON, dragging a node pins
  // it to an absolute pixel position (the legacy customPositions path) instead of reordering.
  // Session-only (not persisted per pedigree). The ref is read inside the d3 drag handler (so a
  // toggle takes effect without re-binding handlers); the state drives the button's pressed look.
  const [freehandMode, setFreehandMode] = React.useState(false);
  const freehandModeRef = React.useRef(false);
  const toggleFreehandMode = React.useCallback(() => {
    setFreehandMode((prev) => {
      const next = !prev;
      freehandModeRef.current = next; // keep ref in sync synchronously for the next drag
      return next;
    });
  }, []);

  // Person-merge state
  const [mergeSelectPersons, setMergeSelectPersons] = React.useState(null);
  const [mergeWizardPersons, setMergeWizardPersons] = React.useState(null);

  // Delete-confirmation target for the Delete/Backspace shortcut — the same
  // typed-UPN confirmation dialog the right-click "Delete" menu item opens.
  const [deleteNodeTarget, setDeleteNodeTarget] = React.useState(null);

  // Migrate-members state: holds the resolved member objects to migrate.
  const [migrateMembers, setMigrateMembers] = React.useState(null);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const selectedIdsRef = useRef(new Set());
  React.useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // FU9 — persist this tab's view state (selected node + which sidebar section is
  // open) up to the workspace, debounced, so a page refresh can restore it. Uses
  // the stable updateViewState callback (not the ws object) to avoid re-render
  // loops; only in workspace mode.
  React.useEffect(() => {
    if (!instanceId || !wsUpdateViewState) return;
    const t = setTimeout(() => {
      const selectedNodeId =
        selectedIds.size === 1 ? [...selectedIds][0] : null;
      wsUpdateViewState(instanceId, {
        selectedNodeId,
        sidebar: {
          open: !!sidebar.open,
          expandedSection: sidebar.expandAccordian
        }
      });
    }, 400);
    return () => clearTimeout(t);
  }, [
    instanceId,
    wsUpdateViewState,
    selectedIds,
    sidebar.open,
    sidebar.expandAccordian
  ]);

  // FU9 — on first mount of a RESTORED tab, re-apply the persisted selection so a
  // refresh returns to the node the clinician was on (the highlight effect paints
  // it once the nodes render). Runs once; reads the persisted view state present
  // at mount. Sidebar/camera visual restore is deferred (needs the node record +
  // live zoom; see PLAN).
  const viewStateRestoredRef = useRef(false);
  React.useEffect(() => {
    if (viewStateRestoredRef.current || !instanceId || !workspace) return;
    viewStateRestoredRef.current = true;
    const vs = workspace.state.sessions.find(
      (s) => s.sessionId === instanceId
    )?.viewState;
    if (vs?.selectedNodeId) setSelectedIds(new Set([vs.selectedNodeId]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Lasso is disabled while a node is open (sidebar/menu showing) or while
  // selectionMode (Add Relation) is active, so its background drag doesn't
  // interrupt a user mid-task. Read by Canvas.jsx via the ref.
  // TGCLOUD-1457: lasso stays ENABLED in move (freehand) mode — selection tools must work
  // identically there (Kathryn lost multi-select the moment she toggled move mode). The
  // lasso is a shift+background drag, so it can't conflict with node drags.
  const lassoEnabledRef = useRef(true);
  React.useEffect(() => {
    lassoEnabledRef.current = !patient && !isLocked && !menu && !selectionMode;
  }, [patient, isLocked, menu, selectionMode]);

  const handleLassoSelect = React.useCallback(
    (pids) => {
      setMenu(false);
      setEdgeMenu(null);
      setSidebar({ open: false, expand: false, expandAccordian: [] });
      if (prevNode.current) {
        d3.select(prevNode.current).attr('stroke-width', 10);
        d3.select(prevNode.current).style('stroke', theme.appearance.text);
        setData(null);
      }
      setSelectedIds(new Set(pids));
    },
    [theme.appearance.text]
  );

  const location = useLocation();
  const appliedNavKeyRef = useRef(null);
  const navPidRef = useRef(null);
  const locationMemberId = location.state?.selectedMemberId ?? null;
  const locationGNum = location.state?.selectedFamilyMemberId ?? null;

  React.useEffect(() => {
    prevNode.current = null;
    appliedNavKeyRef.current = null;
    setData(null);
    setSidebar({ open: false, expand: false, expandAccordian: [] });
  }, [user?._id]);

  React.useEffect(() => {
    const navKey = locationGNum || locationMemberId;
    if (
      !navKey ||
      appliedNavKeyRef.current === navKey ||
      memberRecords.length === 0
    )
      return;
    const member = locationGNum
      ? memberRecords.find(
          (m) => m.pid !== 1 && m.familyMemberId === locationGNum
        ) ||
        memberRecords.find(
          (m) =>
            m.familyMemberId === locationGNum || m.proBandId === locationGNum
        )
      : memberRecords.find((m) => String(m._id) === String(locationMemberId));
    if (member?.pid != null) {
      appliedNavKeyRef.current = navKey;
      navPidRef.current = member.pid;
      setSelectedIds(new Set([member.pid]));
      setData(member);
      setSidebar((prev) => ({
        ...prev,
        open: true,
        expandAccordian: [SIDEBAR_VALUE.recordStatus]
      }));
    }
  }, [memberRecords, locationMemberId, locationGNum]);

  const urlHighlightGNum = React.useMemo(
    () => new URLSearchParams(location.search).get('highlight'),
    [location.search]
  );
  const appliedHighlightRef = useRef(null);
  React.useEffect(() => {
    if (
      !urlHighlightGNum ||
      appliedHighlightRef.current === urlHighlightGNum ||
      memberRecords.length === 0
    )
      return;
    const member =
      memberRecords.find(
        (m) => m.pid !== 1 && m.familyMemberId === urlHighlightGNum
      ) ||
      memberRecords.find(
        (m) =>
          m.familyMemberId === urlHighlightGNum ||
          m.proBandId === urlHighlightGNum
      );
    if (member?.pid != null) {
      appliedHighlightRef.current = urlHighlightGNum;
      navPidRef.current = member.pid;
      setSelectedIds(new Set([member.pid]));
      setData(member);
      setSidebar((prev) => ({ ...prev, open: true, expandAccordian: [] }));
    }
  }, [memberRecords, urlHighlightGNum]);

  // Person lock indicators: stable ref + version counter for Canvas.
  // lockedPersonsRef holds ALL locks; lockedByOtherRef holds only locks by other users.
  const lockedPersonsRef = useRef(new Map());
  const lockedByOtherRef = useRef(new Map());
  const canvasActionsRef = useRef({});
  // The node the user is currently acting on (selected / context-menu target). When an
  // add grows the tree, Canvas keeps THIS node fixed on screen so the clinician doesn't
  // lose focus of what they were editing (Phase A reanchor).
  const reanchorPidRef = useRef(null);
  // D7: one-shot orientation seed — Auto-arrange stores the order it is about to
  // clear so the next (unlocked) re-optimise keeps the displayed handedness.
  const orientationSeedRef = useRef(null);
  // TGCLOUD-1457: set by a pin-only drop (move-mode / group drag) so Canvas's layout
  // effect skips the pointless relayout (the drag already moved everything live).
  const skipPinRelayoutRef = useRef(false);

  useImperativeHandle(ref, () => ({
    openFamilyRecord: () => {
      setData(null);
      setSidebar((prev) => ({ ...prev, open: true }));
    },
    openBulkEdit: () => setBulkEditOpen(true),
    openFamilyHistory: () => {
      setData(null);
      setSidebar((prev) => ({
        ...prev,
        open: true,
        expandAccordian: [SIDEBAR_VALUE.familyHistory],
        scrollTo: SIDEBAR_VALUE.familyHistory
      }));
    },
    // Programmatically set the multi-selection (e.g. the cascade-screening
    // candidate picker). Reuses the same path as the lasso so the result is
    // indistinguishable from a user-built shift-click selection.
    applySelection: (pids) => handleLassoSelect(Array.from(pids || [])),
    // UNDO/REDO cross-layer contract (plan v1.1 §2.1/§4): PedigreeChart's
    // applyLayoutState must invalidate any in-flight worker layout and stash the
    // outgoing order as a one-shot orientation seed — both live at/below ChartBox.
    invalidateLayout: () => canvasActionsRef.current?.invalidateLayout?.(),
    stashOrientationSeed: (order) => {
      orientationSeedRef.current = order;
    }
  }));

  // ARRANGE BEACON: the last layout reported a strictly-better from-scratch
  // arrangement exists → the Auto-arrange button pulses (user drives the change).
  const [arrangeHint, setArrangeHint] = React.useState(false);
  const [lockedPersonsVersion, setLockedPersonsVersion] = React.useState(0);
  React.useEffect(() => {
    if (!personLockHook) return;
    lockedPersonsRef.current = personLockHook.lockedPersons;
    const userId = loggedInUser?._id ? String(loggedInUser._id) : null;
    const otherOnly = new Map();
    personLockHook.lockedPersons.forEach((lockInfo, pid) => {
      if (String(lockInfo.lockedByUserId) !== userId) {
        otherOnly.set(pid, lockInfo);
      }
    });
    lockedByOtherRef.current = otherOnly;
    setLockedPersonsVersion((v) => v + 1);
  }, [personLockHook?.lockedPersons]);

  // Sync data state with latest memberRecords when members change.
  React.useEffect(() => {
    const navPid = navPidRef.current;
    navPidRef.current = null;

    const targetPid = navPid ?? data?.pid;
    if (!targetPid) return;

    const updatedNode = memberRecords.find((m) => m.pid === targetPid);
    if (!updatedNode) return;

    if (navPid != null) {
      setData(updatedNode);
    } else if (data && data.pid) {
      const hasChanges =
        JSON.stringify({
          childrenIds: data.childrenIds,
          spouseIds: data.spouseIds,
          siblingIds: data.siblingIds,
          fatherId: data.fatherId,
          motherId: data.motherId,
          spouseRelationships: data.spouseRelationships,
          dob: data.dob
        }) !==
        JSON.stringify({
          childrenIds: updatedNode.childrenIds,
          spouseIds: updatedNode.spouseIds,
          siblingIds: updatedNode.siblingIds,
          fatherId: updatedNode.fatherId,
          motherId: updatedNode.motherId,
          spouseRelationships: updatedNode.spouseRelationships,
          dob: updatedNode.dob
        });

      if (hasChanges) {
        setData((prev) => ({ ...prev, ...updatedNode }));
      }
    }
  }, [memberRecords]);

  // Re-apply highlight after Canvas re-renders (multi-select aware)
  React.useEffect(() => {
    // Use setTimeout to ensure Canvas useEffect has completed rendering
    const timeoutId = setTimeout(() => {
      // Scope every node lookup to THIS chart's container. pids are per-family
      // and reused, so a document-wide selector would clear/repaint nodes in
      // another mounted chart (multi-pedigree-tabs). Falls back to `document`
      // for a lone chart before its container ref is wired.
      const root = pedRef?.current || document;
      // Clear all node outlines first
      d3.select(root)
        .selectAll('path.te')
        .attr('stroke-width', 10)
        .style('stroke', theme.appearance.text);
      // Re-apply lock indicators (red bold border) — only for locks held by others
      if (lockedByOtherRef.current?.size) {
        lockedByOtherRef.current.forEach((_lockInfo, pid) => {
          const gEl = root.querySelector(`g[data-selection-pid="${pid}"]`);
          if (gEl) {
            const pathEl = gEl.querySelector('path.te');
            if (pathEl) {
              d3.select(pathEl)
                .attr('stroke-width', 20)
                .style('stroke', '#d32f2f');
            }
          }
        });
      }
      // Re-apply highlight for every selected node (selection overrides lock colour)
      selectedIds.forEach((pid) => {
        const gEl = root.querySelector(`g[data-selection-pid="${pid}"]`);
        if (gEl) {
          const pathEl = gEl.querySelector('path.te');
          if (pathEl) {
            d3.select(pathEl)
              .attr('stroke-width', 20)
              .style('stroke', theme.accent);
          }
        }
      });
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [memberRecords, selectedIds, lockedPersonsVersion, customPositions]);

  // Set-Relation pending completion: SetRelation hands us {node, relation,
  // complete} on entry; the second click is routed through handleClick (below)
  // so it survives any SVG rebuild (the old imperative d3 bind did not — a
  // mid-selection relayout froze all node clicks permanently).
  const pendingRelationRef = React.useRef(null);
  const handleSelectionExit = (state) => {
    setSelectionMode(!!state);
    pendingRelationRef.current =
      state && typeof state === 'object' ? state : null;
  };

  const cancelSelectionMode = React.useCallback(() => {
    selectionModeRef.current = false;
    pendingRelationRef.current = null;
    setSelectionMode(false);
    // Fresh selection at call time — the entry highlight/fill may sit on a
    // rebuilt SVG. Scoped to this chart's container so it can't repaint another
    // mounted chart's background.
    d3.select(pedRef?.current || document)
      .select('.canvas')
      .style('fill', 'white');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // UNDO/REDO: one checkpoint per arrow-nudge BURST — the first nudge records the
  // pre-burst state, and the burst window resets after 400ms idle (plan §2.3). A
  // ref (not state) so held-key repeats never re-render or re-bind the handler.
  const nudgeBurstTimerRef = useRef(null);
  // An undo/redo replaces the state UNDER an open burst window: the next nudge
  // must start a fresh step (checkpoint again) or it would ride the stale window
  // and mutate with no checkpoint at all. Reset on every undo/redo trigger.
  const resetNudgeBurst = React.useCallback(() => {
    if (nudgeBurstTimerRef.current != null) {
      clearTimeout(nudgeBurstTimerRef.current);
      nudgeBurstTimerRef.current = null;
    }
  }, []);

  // Keyboard shortcuts: Escape, Ctrl+A, Ctrl/Cmd+Z / Ctrl+Y (undo/redo), arrow nudge
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      // With several charts kept mounted (multi-pedigree-tabs), only the visible
      // one may respond to these window-level shortcuts — otherwise Ctrl+A /
      // arrow-nudge / undo would fire on every open chart at once. A lone chart is
      // always active (isActiveRef defaults true), so single-pedigree use is
      // unchanged.
      if (!isActiveRef.current) return;
      // Auto-repeat must never drive a mutation: holding a letter would otherwise
      // fire applyAdd dozens of times, and the keyboard path does not go through
      // AddRelativeMenu's `guarded` double-fire protection (develop's Phase 1
      // interaction hardening). Escape/undo/zoom are also fine to ignore on repeat.
      if (e.repeat) return;
      // Escape keeps its historical behavior even while a form field is focused —
      // the selection-mode banner instructs "press Escape", and it must work
      // mid-edit exactly as it did before the focus guard below existed.
      if (e.key === 'Escape') {
        if (selectionModeRef.current) {
          cancelSelectionMode();
          return;
        }
        setSelectedIds(new Set());
        return;
      }
      // Never hijack the remaining shortcuts while the user is typing in a form
      // field (sidebar edit fields, dialogs, search boxes): before this guard,
      // Ctrl+A stole select-all from a focused input and selected every node.
      if (isEditableTarget(e.target)) {
        return;
      }
      // isEditableTarget was written when the only shortcuts were Ctrl/Cmd chords
      // ("before this guard, Ctrl+A stole select-all from a focused input").
      // TGCLOUD-1541 broadened the trigger set to ~20 BARE letters without
      // broadening the guard, so a letter pressed while a dialog is open — with
      // focus on the dialog rather than an input — reached the pedigree underneath
      // and mutated it. Treat any open modal/dialog as off-limits.
      if (
        e.target?.closest?.('[role="dialog"]') ||
        document.querySelector('.MuiModal-root, [role="dialog"]')
      ) {
        return;
      }
      const undoRedoZ =
        (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z');
      const redoY = e.ctrlKey && !e.metaKey && e.key === 'y';
      if (layoutConfig.ENABLE_UNDO_REDO && (undoRedoZ || redoY)) {
        e.preventDefault();
        resetNudgeBurst();
        // Ctrl/Cmd+Z = undo; Ctrl/Cmd+Shift+Z and Ctrl+Y = redo. Plain Cmd+Y is
        // NOT claimed — it is the browser-history shortcut on macOS. The handlers
        // no-op when the pedigree is locked by another user / patient mode.
        if (e.key === 'y' || e.shiftKey) onHistoryRedo?.();
        else onHistoryUndo?.();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedIds(new Set(canvasMembers.map((m) => m.pid)));
      } else if (
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) &&
        selectedIds.size > 0 &&
        // Nudging writes customPositions, so it is a mutation and must respect the
        // same read-only gate as every other edit surface. See the isLocked note
        // below and in PedigreeChartView.
        !isLocked
      ) {
        e.preventDefault();
        if (onHistoryCheckpoint) {
          if (nudgeBurstTimerRef.current == null) onHistoryCheckpoint();
          else clearTimeout(nudgeBurstTimerRef.current);
          nudgeBurstTimerRef.current = setTimeout(() => {
            nudgeBurstTimerRef.current = null;
          }, 400);
        }
        // TGCLOUD-1457: Shift+arrow = FINE nudge (25px) for precise spacing adjustments;
        // plain arrow keeps the coarse two-grid-unit step.
        const step = e.shiftKey ? 25 : 200;
        const dx =
          e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy =
          e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        setCustomPositions((prev) => {
          const newMap = new Map(prev);
          selectedIds.forEach((pid) => {
            // Pins are keyed by UID everywhere (the engine looks them up by uid) — the
            // old code keyed by PID, so nudged positions never survived a re-render
            // (TGCLOUD-1457). Resolve the member's uid first.
            const member = canvasMembers.find((m) => m.pid === pid);
            const uid = member?.uid;
            if (uid == null) return;
            const existing = prev.get(uid);
            // A nudge needs a finite pixel base to add dx/dy to. A grid-only pin
            // ({gx,gy} with no legacy x/y) has none — the old code computed
            // existing.x + dx = NaN AND deleted gx (gap underivable), leaving a
            // {x: NaN, y: NaN} pin whose node then rendered with broken/missing
            // relationship lines (G-1-01391). Such pins fall through to the
            // DOM-read branch below, which nudges from the rendered position.
            if (
              existing &&
              Number.isFinite(existing.x) &&
              Number.isFinite(existing.y)
            ) {
              // D2: keep the grid-unit representation consistent through the
              // nudge (derive the pin's own gap from its stored pair); if that
              // isn't derivable (x/y of 0), drop to a pixel pin — Canvas's
              // one-shot upgrade re-adds units on the next layout pass.
              const nx = existing.x + dx;
              const ny = existing.y + dy;
              const next = { ...existing, x: nx, y: ny };
              if (existing.gx != null) {
                const gapX =
                  existing.x && existing.gx ? existing.x / existing.gx : null;
                if (gapX) next.gx = nx / gapX;
                else delete next.gx;
              }
              if (existing.gy != null) {
                const gapY =
                  existing.y && existing.gy ? existing.y / existing.gy : null;
                if (gapY) next.gy = ny / gapY;
                else delete next.gy;
              }
              newMap.set(uid, next);
            } else {
              // Read current position from DOM transform — scoped to this chart's
              // container so a reused pid can't read another mounted chart's node.
              const gEl = (pedRef?.current || document).querySelector(
                `g[data-selection-pid="${pid}"]`
              );
              if (gEl) {
                const t = gEl.getAttribute('transform') || '';
                const m = t.match(/translate\(([^,]+),([^)]+)\)/);
                if (m) {
                  newMap.set(uid, {
                    uid,
                    vx: 0,
                    vy: 0,
                    x: parseFloat(m[1]) + dx,
                    y: parseFloat(m[2]) + dy
                  });
                }
              }
            }
          });
          return newMap;
        });
      } else {
        const shortcut = shortcutsEnabled ? resolveShortcut(e) : null;
        // A proxy is a RENDER-only clone of a real member; the sidebar must be
        // bound to the OWNING record, exactly as the mouse path does in
        // handleClick. Passing the raw canvas node instead binds the editor to a
        // synthetic pid, so the clinician's edits are written against a member
        // that does not exist (silently discarded) and a person lock is taken on
        // that phantom pid. `canvasPid` keeps the highlight/un-highlight keyed to
        // the node actually on screen.
        const bindSidebarTo = (rawNode) => {
          if (!rawNode) return setData(null);
          const ownerPid =
            rawNode.isProxy && rawNode.proxyOf != null
              ? rawNode.proxyOf
              : rawNode.pid;
          const record = memberRecords.find((m) => m.pid === ownerPid);
          setData({ ...rawNode, ...(record || {}), canvasPid: rawNode.pid });
        };
        if (shortcut?.type === 'addRelative') {
          // isLocked is develop's single read-only enforcement point: it gates
          // onSumbit, hides Bulk edit, and disables undo/redo, lasso and the node
          // menus (see the comment in PedigreeChartView). The keyboard shortcuts
          // added a SIXTH mutation surface that was not on that list — without
          // this guard, a read-only FHQ preview or a pedigree locked by another
          // clinician could still be edited from the keyboard.
          if (isLocked) return;
          e.preventDefault();
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode && shortcut.relative !== 'unknown') return;

          const node = rawNode
            ? { ...rawNode, kind: layoutConfig.NODE_TYPE.NODE }
            : null;

          const allowedActions = getAllowedRelativeActions(node);
          const applyAdd = (fn) => {
            setMemberRecords((prev) => fn(prev, node, user));
            setPedigreeState((prev) => ({
              ...prev,
              save: PEDIGREE.saveStatus.IN_PROGRESS
            }));
          };

          switch (shortcut.relative) {
            case 'son':
              if (allowedActions.has(RELATIVE_ACTIONS.son)) applyAdd(addSon);
              break;
            case 'daughter':
              if (allowedActions.has(RELATIVE_ACTIONS.daughter))
                applyAdd(addDaughter);
              break;
            case 'unknown':
              applyAdd(addUnknown);
              break;
            case 'spouse':
              if (allowedActions.has(RELATIVE_ACTIONS.spouse))
                applyAdd(addSpouse);
              break;
            case 'brother': {
              const hasParents = !!(node.fatherId || node.motherId);
              if (hasParents && allowedActions.has(RELATIVE_ACTIONS.brother))
                applyAdd(addBrother);
              break;
            }
            case 'sister': {
              const hasParents = !!(node.fatherId || node.motherId);
              if (hasParents && allowedActions.has(RELATIVE_ACTIONS.sister))
                applyAdd(addSister);
              break;
            }
            case 'parents': {
              const hasParents = !!(node.fatherId || node.motherId);
              if (!hasParents && allowedActions.has(RELATIVE_ACTIONS.parents))
                applyAdd(addParents);
              break;
            }
            case 'grandparents': {
              const hasBothParents = !!(node.fatherId && node.motherId);
              const grandparentsExist =
                hasBothParents &&
                (() => {
                  const father = memberRecords.find(
                    (p) => p.pid === node.fatherId
                  );
                  const mother = memberRecords.find(
                    (p) => p.pid === node.motherId
                  );
                  return !!(
                    (father && (father.fatherId || father.motherId)) ||
                    (mother && (mother.fatherId || mother.motherId))
                  );
                })();
              if (
                !grandparentsExist &&
                allowedActions.has(RELATIVE_ACTIONS.grandparents)
              )
                applyAdd(addGrandparents);
              break;
            }
            default:
              break;
          }
        } else if (shortcut?.type === 'toggleGrid') {
          e.preventDefault();
          setOptions((prev) => ({ ...prev, enable_grid: !prev?.enable_grid }));
        } else if (shortcut?.type === 'zoomIn') {
          e.preventDefault();
          canvasActionsRef.current?.zoomIn?.();
        } else if (shortcut?.type === 'zoomOut') {
          e.preventDefault();
          canvasActionsRef.current?.zoomOut?.();
        } else if (shortcut?.type === 'help') {
          e.preventDefault();
          setShortcutsHelpOpen(true);
        } else if (shortcut?.type === 'panel') {
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode && PANELS_NEEDING_NODE.has(shortcut.panel)) return;
          e.preventDefault();
          bindSidebarTo(rawNode || null);
          setSidebar((prev) => ({
            ...prev,
            open: true,
            expandAccordian: [shortcut.panel]
          }));
        } else if (shortcut?.type === 'editName') {
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          if (!rawNode) return;
          e.preventDefault();
          bindSidebarTo(rawNode);
          setSidebar((prev) => ({
            ...prev,
            open: true,
            expandAccordian: [SIDEBAR_VALUE.person]
          }));
        } else if (shortcut?.type === 'delete') {
          // Same read-only gate as the addRelative branch above: this opens the
          // confirm modal that issues a real server-side delete.
          if (isLocked) return;
          const pid = selectedIds.size === 1 ? [...selectedIds][0] : null;
          const rawNode =
            pid != null
              ? canvasMembersWithProxies.find((m) => m.pid === pid)
              : null;
          // Mirrors the right-click "Delete Person" gating in ContextMenu:
          // proxies delete via a different flow (Remove Proxy), and the
          // active proband can't be deleted directly (move/remove the
          // proband designation first).
          if (!rawNode || rawNode.isProxy) return;
          const activeProbandPid = getProbandState(canvasMembers).probandPid;
          if (
            activeProbandPid != null &&
            String(rawNode.pid) === String(activeProbandPid)
          )
            return;
          e.preventDefault();
          setDeleteNodeTarget(rawNode);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    selectedIds,
    canvasMembers,
    canvasMembersWithProxies,
    memberRecords,
    user,
    shortcutsEnabled,
    isLocked,
    cancelSelectionMode,
    onHistoryCheckpoint,
    onHistoryUndo,
    onHistoryRedo,
    resetNudgeBurst
  ]);

  const handleClose = () => {
    setMenu(false);
    setEdgeMenu(null);
    setEdgeAnchorEl(null);

    setSidebar({
      open: false,
      expand: false,
      expandAccordian: []
    });

    if (prevNode.current) {
      d3.select(prevNode.current).attr('stroke-width', 10);
      d3.select(prevNode.current).style('stroke', theme.appearance.text);
      setData(null);
    }
    setSelectedIds(new Set());
  };

  const handleClick = (event, d, nodeRef, panels) => {
    // Remember the node being acted on so a subsequent add keeps it framed (Phase A).
    if (d?.pid != null) reanchorPidRef.current = d.pid;

    // Set-Relation second click: complete (or exit, on the same node) via the
    // persistent click path — rebuilt SVGs keep working. If the mode is somehow
    // active with no pending relation (legacy/orphaned state), recover by
    // cancelling instead of swallowing clicks forever.
    if (selectionModeRef.current) {
      const pending = pendingRelationRef.current;
      if (pending?.complete) {
        Promise.resolve(pending.complete(d)).finally(cancelSelectionMode);
      } else {
        cancelSelectionMode();
      }
      return;
    }

    const isModifier =
      event && (event.shiftKey || event.ctrlKey || event.metaKey);

    if (isModifier && !patient && !selectionModeRef.current && !isLocked) {
      // Toggle this node in the multi-selection
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(d.pid)) {
          next.delete(d.pid);
        } else {
          next.add(d.pid);
        }
        return next;
      });
      return;
    }

    // Plain click: reset to single-node selection
    if (prevNode.current) {
      d3.select(prevNode.current).attr('stroke-width', 10);
      d3.select(prevNode.current).style('stroke', theme.appearance.text);
    }
    // Allow sidebar to open for all non-patient users (even when pedigree is
    // locked by another — sidebar shows read-only view in that case)
    if (!patient && !selectionModeRef.current) {
      // Make selected node highlighted
      d3.select(nodeRef).attr('stroke-width', 20);
      d3.select(nodeRef).style('stroke', theme.accent);
      prevNode.current = nodeRef;
      const ownerPid = d.isProxy && d.proxyOf != null ? d.proxyOf : d.pid;
      const record = memberRecords.find((m) => m.pid === ownerPid);
      setData({ ...d, ...(record || {}), canvasPid: d.pid, nodeRef: nodeRef });
      setSelectedIds(new Set([d.pid]));

      // Reproductive nodes (pregnancy / gamete donor) are pedigree elements, not persons —
      // they get no person sidebar of their own. Clicking one redirects to the CARRIER's
      // sidebar with the Reproductive accordion expanded (mirrors person-click → sidebar),
      // so the pregnancy is editable in context. Resolve the carrier via the record id in
      // the node uid (pregnancy uid = record._id; donor uid = `<record._id>:<index>`).
      if (layoutConfig.ENABLE_REPRO_NODES && isReproNode(d)) {
        const recordId = String(d.uid ?? '').split(':')[0];
        const carrier = (memberRecords || []).find((m) =>
          (m.pregnancies || []).some((p) => String(p._id) === recordId)
        );
        // Undo the highlight just applied to the repro node; the selection effect
        // highlights the carrier from selectedIds instead.
        d3.select(nodeRef)
          .attr('stroke-width', 10)
          .style('stroke', theme.appearance.text);
        prevNode.current = null;
        if (carrier) {
          setData({ ...carrier });
          setSelectedIds(new Set([carrier.pid]));
          setSidebar((pre) => ({
            ...pre,
            open: true,
            expandAccordian: [SIDEBAR_VALUE.reproductive]
          }));
        } else {
          setSidebar((pre) => ({ ...pre, open: false }));
        }
        return;
      }

      const prevPanel =
        localStorage.getItem('sidebar-panel') || SIDEBAR_VALUE.addRelative;

      setSidebar((pre) => ({
        ...pre,
        open: true,
        expandAccordian: panels ? panels : [prevPanel] // Make Legend & Add Relative default expanded
      }));
      // Signal for the Clinician Copilot guided walkthrough (read-only, no
      // payload beyond the type — no pid, no person data leaves this module).
      // Advances a walkthrough step whose advanceOn is 'node-selected'. No-op
      // when no tour is running.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('va:pedigree', { detail: { type: 'node-selected' } })
        );
      }
    }
  };

  // NOTE: canvasMembersWithProxies is declared once, higher up (just after
  // `proxies`). Both branches added an identical memo — develop here, sprint-94
  // earlier — and auto-merge kept both, which is a duplicate-declaration error.
  // The EARLIER position is the one that must survive: sprint-94's keyboard
  // shortcut effect lists it in a dependency array that is evaluated during
  // render well above this line, so declaring it only here throws
  // "Cannot access 'canvasMembersWithProxies' before initialization".
  const eligiblePids = React.useMemo(
    () => getEligiblePids(canvasMembersWithProxies),
    [canvasMembersWithProxies]
  );

  // REQ-I6 navigation. The sidebar can ask for a member to be SELECTED — "go to the child born of
  // this pregnancy", or back to the carrier whose record this person was born of. Selection lives
  // here (data + selectedIds + the canvas highlight), so the sidebar posts a pid and this performs
  // it, then clears the request so it fires once.
  React.useEffect(() => {
    const pid = sidebar?.selectPid;
    if (pid == null) return;
    setSidebar((pre) => ({ ...pre, selectPid: null }));
    const member = (memberRecords || []).find((m) => m.pid === pid);
    if (!member) return;
    setData({ ...member });
    setSelectedIds(new Set([pid]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar?.selectPid, memberRecords]);

  // Double-click a fetus/pregnancy symbol → open THAT record's editor (REQ-H4; Desktop's own
  // confirmation tells the user to "double click on the fetus icon", so the affordance has to
  // exist). Rather than mounting a second copy of the editor here, this selects the carrier and
  // asks the sidebar's Reproductive accordion — the editor's existing, proven host — to open the
  // modal for that record. The single click has already run and selected the carrier, so the two
  // compose.
  const onReproNodeDoubleClick = (d) => {
    if (isLocked) return;
    const recordId = String(d?.uid ?? '').split(':')[0];
    if (!recordId) return;
    const carrier = (memberRecords || []).find((m) =>
      (m.pregnancies || []).some((p) => String(p._id) === recordId)
    );
    if (!carrier) return;
    setData({ ...carrier });
    setSelectedIds(new Set([carrier.pid]));
    setSidebar((pre) => ({
      ...pre,
      open: true,
      expandAccordian: [SIDEBAR_VALUE.reproductive],
      openReproRecordId: recordId
    }));
  };

  const onNodeClick = (e, d) => {
    if (selectionModeRef.current) return;
    if (isLocked) return;
    // Right-click / context-menu target is also an add anchor (Phase A reanchor).
    if (d?.pid != null) reanchorPidRef.current = d.pid;
    // Allow the menu with >2 selected only when the selection is a migratable
    // (fully independent, all-eligible) group — its only action is Migrate.
    if (selectedIds.size > 2) {
      const { independent } = getMigratablePidsDetail(
        canvasMembersWithProxies,
        selectedIds
      );
      if (!independent) return;
    }
    setMenu(d);
    setAnchorEl(e.currentTarget);
  };

  const handleMigrate = React.useCallback(
    (node) => {
      const pids =
        selectedIds.size > 0
          ? [...selectedIds]
          : [...getComponentOf(canvasMembersWithProxies, node?.pid)].map(
              (p) => {
                const n = Number(p);
                return Number.isNaN(n) ? p : n;
              }
            );
      const pidSet = new Set(pids.map((p) => String(p)));
      const resolved = (canvasMembers || []).filter((m) =>
        pidSet.has(String(m.pid))
      );
      setMenu(false);
      setAnchorEl(null);
      // Clinical-proband guard via the tri-state selector (see
      // pedigree/probandState.js): in the 'cleared' state probandPid is null
      // — there is no active proband to protect. The structural root stays
      // un-migratable regardless, via the independent migrateUtil eligibility
      // check below.
      const activeProbandPid = getProbandState(canvasMembers).probandPid;
      const probandPid =
        activeProbandPid != null ? String(activeProbandPid) : '';
      const probandInSelection =
        probandPid !== '' && resolved.some((m) => String(m.pid) === probandPid);
      if (probandInSelection) {
        setError(
          'The selection includes the proband which cannot be migrated. Deselect the proband and try again.'
        );
        return;
      }
      const { independent } = getMigratablePidsDetail(
        canvasMembersWithProxies,
        new Set(pids)
      );
      if (!independent || resolved.length === 0) {
        const details = getMigratablePidsDetail(
          canvasMembersWithProxies,
          new Set(pids)
        );
        const ineligiblePids = pids
          .map((p) => String(p))
          .filter((p) => !details.eligible.has(p));
        const reasonParts = [];
        if (ineligiblePids.length > 0)
          reasonParts.push(`ineligible: ${ineligiblePids.join(', ')}`);
        if (details.danglingPids.length > 0)
          reasonParts.push(`linked-to: ${details.danglingPids.join(', ')}`);
        if (resolved.length === 0) reasonParts.push('no-resolved-members');
        setError(
          reasonParts.length > 0
            ? `Only a fully independent group (with no links to other members) can be migrated. Adjust your selection. (${reasonParts.join(
                ' | '
              )})`
            : 'Only a fully independent group (with no links to other members) can be migrated. Adjust your selection.'
        );
        return;
      }
      setMigrateMembers(resolved);
    },
    [selectedIds, canvasMembers, canvasMembersWithProxies]
  );

  const refreshSourcePedigree = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
    queryClient.invalidateQueries({ queryKey: ['probandUsers'] });
    window.dispatchEvent(new CustomEvent('clinical-notes-refresh'));
    axios
      .get(APICONSTANTS.getPedigreeDetails.replace(':familyId', user?.familyId))
      .then(({ data }) => {
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: {
            family: data.families,
            selectedUser: data.proband,
            perf: data.perf
          }
        });
      })
      .catch(() => {});
  }, [queryClient, user?.familyId, dispatchUpdate]);

  const onMigrateSuccess = React.useCallback(() => {
    const migratedPids = new Set((migrateMembers || []).map((m) => m.pid));
    if (migratedPids.size > 0) {
      setMemberRecords((prev) =>
        prev
          .filter((p) => !migratedPids.has(p.pid))
          .map((p) =>
            [...migratedPids].reduce(
              (member, pid) => stripMemberReferencesToPid(member, pid),
              p
            )
          )
      );
    }
    setMigrateMembers(null);
    setSelectedIds(new Set());
    refreshSourcePedigree();
  }, [migrateMembers, setMemberRecords, refreshSourcePedigree]);

  const handleMergeTwoSelected = React.useCallback(() => {
    if (anonymousPedigrees) return;
    const [pid1, pid2] = [...selectedIds];
    const node1 = canvasMembers.find((m) => m.pid === pid1);
    const node2 = canvasMembers.find((m) => m.pid === pid2);
    if (!node1 || !node2) return;
    const toPersonRef = (nodeData) => ({
      firstName: nodeData?.firstName,
      lastName: nodeData?.lastName,
      dob: nodeData?.dob,
      upn: nodeData?.upn,
      familyMemberId: nodeData?.familyMemberId || nodeData?.proBandId,
      familyId: user?.familyId
    });
    setMenu(false);
    setAnchorEl(null);
    setMergeSelectPersons({
      personA: toPersonRef(node1),
      personB: toPersonRef(node2)
    });
  }, [selectedIds, canvasMembers, user?.familyId, anonymousPedigrees]); // eslint-disable-line react-hooks/exhaustive-deps

  const onEdgeClick = (e, d) => {
    if (isLocked) return;
    setEdgeMenu(d);
    setEdgeAnchorEl(e.currentTarget);
  };

  const handleEdgeClose = () => {
    setEdgeMenu(null);
    setEdgeAnchorEl(null);
  };

  const handleCloseDialog = () => {
    setError(false);
  };

  const contextMenu = React.useMemo(() => {
    return (
      <ContextMenu
        user={user}
        node={menu}
        setMemberRecords={setMemberRecords}
        memberRecords={memberRecords}
        onCloseMenu={handleClose}
        setPedigreeState={setPedigreeState}
        selectionModeRef={selectionModeRef}
        handleSelectionExit={handleSelectionExit}
        setError={setError}
        customPositions={customPositions}
        setCustomPositions={setCustomPositions}
        selectedCount={selectedIds.size}
        onMergeTwoSelected={handleMergeTwoSelected}
        onAddProxy={handleAddProxy}
        onRemoveProxy={handleRemoveProxy}
        eligiblePids={eligiblePids}
        onMigrate={handleMigrate}
        isSaving={isSaving}
        onHistoryCheckpoint={onHistoryCheckpoint}
        viewHide={viewHide}
        // The DRAWN member set: the hide-connector guard must analyse what the
        // canvas renders (an active proxy severs the original's drawn edges).
        hideAnalysisMembers={canvasMembersWithProxies}
      />
    );
  }, [
    user,
    menu,
    memberRecords,
    customPositions,
    selectedIds,
    viewHide,
    canvasMembersWithProxies,
    handleMergeTwoSelected,
    handleAddProxy,
    handleRemoveProxy,
    eligiblePids,
    handleMigrate,
    isSaving
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // linkStatusMap / refetchLinkStatus are lifted to PedigreeChart (which owns
  // the member list and renders the legend) so the legend and the canvas share
  // one source of truth and the legend can gate its swatches on presence.
  // A badge click selects the node and opens the sidebar Record Status panel —
  // mirrors the selectedFamilyMemberId deep-link (see the location.state effect).
  const onBadgeClick = (event, d, teEl) =>
    handleClick(event, d, teEl, [SIDEBAR_VALUE.recordStatus]);

  const canvas = React.useMemo(() => {
    return (
      <Canvas
        pedRef={pedRef}
        proband={user}
        members={canvasMembersWithProxies}
        labelsSet={labelsSet}
        carrier={carrier}
        carrierStyle={carrierStyle}
        analysis={analysis}
        onNodeClick={onNodeClick}
        onReproNodeDoubleClick={onReproNodeDoubleClick}
        onEdgeClick={onEdgeClick}
        handleClick={handleClick}
        onCloseMenu={handleClose}
        options={options}
        colors={colors}
        customPositions={customPositions}
        setCustomPositions={setCustomPositions}
        establishedOrder={establishedOrder}
        setEstablishedOrder={setEstablishedOrder}
        orderLocked={orderLocked}
        setOrderLocked={setOrderLocked}
        freehandModeRef={freehandModeRef}
        selectedIdsRef={selectedIdsRef}
        lockedPersonsRef={lockedByOtherRef}
        lockedPersonsVersion={lockedPersonsVersion}
        onLassoSelect={handleLassoSelect}
        lassoEnabledRef={lassoEnabledRef}
        canvasActionsRef={canvasActionsRef}
        linkStatusMap={linkStatusMap}
        onBadgeClick={onBadgeClick}
        reanchorPidRef={reanchorPidRef}
        skipPinRelayoutRef={skipPinRelayoutRef}
        orientationSeedRef={orientationSeedRef}
        onArrangeHint={setArrangeHint}
        onHistoryCheckpoint={onHistoryCheckpoint}
        onHistoryResolve={onHistoryResolve}
      />
    );
  }, [
    canvasMembersWithProxies,
    labelsSet,
    carrier,
    carrierStyle,
    analysis,
    customPositions,
    establishedOrder,
    setEstablishedOrder,
    orderLocked,
    options,
    lockedPersonsVersion,
    handleLassoSelect,
    linkStatusMap
  ]);

  const pedigreeSideBar = React.useMemo(() => {
    return (
      <PedigreeSideBar
        data={selectedIds.size > 1 ? null : data}
        user={user}
        setSidebar={setSidebar}
        sidebar={sidebar}
        setData={setData}
        labelsSet={labelsSet}
        setLabelsSet={setLabelsSet}
        memberRecords={memberRecords}
        setMemberRecords={setMemberRecords}
        setPedigreeState={setPedigreeState}
        personLockHook={personLockHook}
        bulkEditActive={bulkEditOpen}
        pedigreeLockedByOther={isLocked && !isMine}
        isMigrated={isMigrated}
        carrierPresentStates={carrier.presentStatesByDisease}
        showCarrierMarks={labelsSet?.showCarrierMarks !== false}
        carrierStyle={carrierStyle.activeStyle}
        analysis={analysis}
        onLinkStatusChanged={refetchLinkStatus}
        isSaving={isSaving}
      />
    );
  }, [
    data,
    sidebar,
    labelsSet,
    memberRecords,
    selectedIds,
    bulkEditOpen,
    isLocked,
    isMine,
    isSaving,
    carrier,
    carrierStyle.activeStyle,
    analysis,
    refetchLinkStatus,
    // Re-render sidebar when person locks change so hasPersonLock / lockedByOther
    // are recomputed after async lock acquisition/release
    personLockHook?.lockedPersons
  ]);

  const enrichedPendingPersonRequest = React.useMemo(() => {
    const req = personLockHook?.pendingPersonRequest;
    if (!req) return null;
    const person = memberRecords.find(
      (m) => String(m.pid) === String(req.personId)
    );
    const personName =
      [person?.firstName, person?.lastName].filter(Boolean).join(' ') || null;
    const personUpn = person?.upn || null;
    return { ...req, personName, personUpn };
  }, [personLockHook?.pendingPersonRequest, memberRecords]);

  // Working-area offset: the region between the left rail and the docked detail
  // panel. Mirrors LockBanner's own bottom-banner offset (panel width 400 when
  // open and not expanded). Only applied to the top chip + stacked save status
  // when the flag is on, so the flag-off layout is unchanged.
  const workingAreaOffset = sidebar.open && !sidebar.expand ? 400 : 0;

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative'
      }}
    >
      <LockBanner
        lockState={lockState}
        isMine={isMine}
        requestStatus={requestStatus}
        onRequestLock={onRequestLock}
        onCancelRequest={onCancelRequest}
        onForceAcquireLock={onForceAcquireLock}
        pendingRequest={pendingRequest}
        onRespondToRequest={onRespondToRequest}
        pendingPersonRequest={enrichedPendingPersonRequest}
        onDismissPersonRequest={personLockHook?.dismissPersonRequest}
        sidebarOpen={sidebar.open}
        sidebarExpand={sidebar.expand}
        centerOffset={ENABLE_STACKED_SAVE_INDICATOR ? workingAreaOffset : 0}
      />
      {/* Transient save status, stacked as a lighter second line directly below
          the "You are editing" chip. Independently positioned + centred on the
          same working area, so its mount/unmount never reflows the chip. */}
      {ENABLE_STACKED_SAVE_INDICATOR && (
        <Box
          sx={{
            position: 'absolute',
            top: 38,
            left: workingAreaOffset
              ? `calc(50% - ${workingAreaOffset / 2}px)`
              : '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
            pointerEvents: 'none'
          }}
        >
          <PedigreeSaveIndicator isSaving={isSaving} />
        </Box>
      )}
      {selectedIds.size > 1 && (
        <Box
          sx={{
            position: 'absolute',
            // Push below the "You are editing" chip when isMine; otherwise sit at top
            top: lockState && isMine ? 48 : 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300
          }}
        >
          <Chip
            icon={<PeopleAlt sx={{ fontSize: 16 }} />}
            data-va-anchor="pedigree.selection.chip"
            label={`${selectedIds.size} members selected`}
            size="small"
            sx={{
              bgcolor: '#e5f6fd',
              color: '#014361',
              fontWeight: 'bold',
              '& .MuiChip-icon': { color: '#014361' }
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          position: 'absolute',
          left: 15,
          top: 12,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          padding: '4px 8px'
        }}
      >
        <Tooltip title="Zoom Out" arrow>
          <IconButton
            size="small"
            onClick={() => canvasActionsRef.current?.zoomOut?.()}
          >
            <RemoveCircle sx={{ fontSize: 22, color: '#3d4551' }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom In" arrow>
          <IconButton
            size="small"
            onClick={() => canvasActionsRef.current?.zoomIn?.()}
          >
            <AddCircle sx={{ fontSize: 22, color: '#3d4551' }} />
          </IconButton>
        </Tooltip>
        <CanvasPreferences
          options={options}
          setOptions={setOptions}
          members={canvasMembersWithProxies}
          inline
        />
        <LocatePerson
          pedref={pedRef}
          members={memberRecords}
          handleClick={handleClick}
        />
        <ClearPosition
          customPositions={customPositions}
          setCustomPositions={setCustomPositions}
          memberRecords={memberRecords}
          onHistoryCheckpoint={onHistoryCheckpoint}
          onHistoryResolve={onHistoryResolve}
        />
        {layoutConfig.ENABLE_UNDO_REDO && (
          <>
            <Tooltip
              title={canUndo ? 'Undo layout change' : 'Nothing to undo'}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    resetNudgeBurst();
                    onHistoryUndo?.();
                  }}
                  disabled={!canUndo}
                  data-va-anchor="pedigree.toolbar.undo"
                  aria-label="Undo layout change"
                >
                  <Undo
                    sx={{
                      fontSize: 22,
                      color: canUndo ? '#3d4551' : undefined
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={canRedo ? 'Redo layout change' : 'Nothing to redo'}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    resetNudgeBurst();
                    onHistoryRedo?.();
                  }}
                  disabled={!canRedo}
                  data-va-anchor="pedigree.toolbar.redo"
                  aria-label="Redo layout change"
                >
                  <Redo
                    sx={{
                      fontSize: 22,
                      color: canRedo ? '#3d4551' : undefined
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        {layoutConfig.ENABLE_ESTABLISHED_ORDER && (
          <AutoArrange
            establishedOrder={establishedOrder}
            customPositions={customPositions}
            setEstablishedOrder={setEstablishedOrder}
            setCustomPositions={setCustomPositions}
            setOrderLocked={setOrderLocked}
            orientationSeedRef={orientationSeedRef}
            onHistoryCheckpoint={onHistoryCheckpoint}
            // 2026-08-12 (Tim's ruling): the beacon lights whenever the
            // engine's from-scratch optimum is strictly better — INCLUDING a
            // hand-arranged (orderLocked) chart. The old `&& !orderLocked`
            // suppression meant one drag-to-reorder permanently blinded the
            // beacon until an Auto-arrange, so a user who deliberately
            // de-optimised the layout got no cue. The hint is advisory (the
            // user still decides when to adopt); a deliberate arrangement the
            // optimiser can't beat never lights it anyway.
            arrangeHint={arrangeHint}
          />
        )}
        {layoutConfig.ENABLE_ESTABLISHED_ORDER && (
          <Tooltip
            title={
              freehandMode
                ? 'Move mode ON — drag to reposition freely (click to return to reorder)'
                : 'Move mode — drag a node to any position'
            }
            arrow
          >
            <IconButton
              size="small"
              onClick={toggleFreehandMode}
              data-va-anchor="pedigree.toolbar.moveMode"
              aria-pressed={freehandMode}
            >
              <OpenWith
                sx={{
                  fontSize: 22,
                  color: freehandMode ? '#1976d2' : '#3d4551'
                }}
              />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Keyboard shortcuts" arrow>
          <IconButton
            size="small"
            onClick={() => setShortcutsHelpOpen(true)}
            aria-label="Keyboard shortcuts"
          >
            <Keyboard
              sx={{
                fontSize: 22,
                color: shortcutsEnabled ? '#3d4551' : '#94a3b8'
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>
      {pedigreeSideBar}
      {selectionMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 5,
            left: 0,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 2200,
            px: 2
          }}
        >
          <Alert
            severity="warning"
            sx={{ fontWeight: 'bold' }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={cancelSelectionMode}
              >
                Cancel
              </Button>
            }
          >
            Entered Selection Mode: click a target node to complete Set
            Relation. Press Esc or Cancel to exit.
          </Alert>
        </Box>
      )}
      {canvas}
      <BulkEditModal
        open={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
        memberRecords={
          selectedIds.size > 1
            ? memberRecords.filter((m) => selectedIds.has(m.pid))
            : memberRecords
        }
        setMemberRecords={setMemberRecords}
        setPedigreeState={setPedigreeState}
        setLabelsSet={setLabelsSet}
        familyId={user?.familyId}
        personLockHook={personLockHook}
      />
      <CustomDialog
        open={!!error}
        title={'Info'}
        handleClose={handleCloseDialog}
        minHeight={100}
        distanceTop={100}
        allowBackgroundClose={true}
        showCancelBtn={true}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Info fontSize="large" />
          {error}
        </Box>
      </CustomDialog>
      <ShortcutsHelp
        open={shortcutsHelpOpen}
        onClose={() => setShortcutsHelpOpen(false)}
        enabled={shortcutsEnabled}
        onToggle={toggleShortcutsEnabled}
      />
      <Popper
        open={!!menu && !selectionMode}
        anchorEl={anchorEl}
        sx={{ zIndex: 1299 }}
      >
        {contextMenu}
      </Popper>
      <Popper open={!!edgeMenu} anchorEl={edgeAnchorEl} sx={{ zIndex: 1299 }}>
        <EdgeContextMenu
          edge={edgeMenu}
          user={user}
          memberRecords={memberRecords}
          setMemberRecords={setMemberRecords}
          onClose={handleEdgeClose}
        />
      </Popper>
      {deleteNodeTarget && (
        <DeleteNode
          node={deleteNodeTarget}
          user={user}
          memberRecords={memberRecords}
          setMemberRecords={setMemberRecords}
          handleClose={() => setDeleteNodeTarget(null)}
        />
      )}
      {mergeSelectPersons && (
        <MergePersonSelectModal
          open={!!mergeSelectPersons}
          personA={mergeSelectPersons.personA}
          personB={mergeSelectPersons.personB}
          onSelect={({ retained, deleted }) => {
            setMergeSelectPersons(null);
            setMergeWizardPersons({ retained, deleted });
          }}
          onClose={() => setMergeSelectPersons(null)}
        />
      )}
      {migrateMembers && (
        <MigrateModal
          open={!!migrateMembers}
          handleClose={() => setMigrateMembers(null)}
          members={migrateMembers}
          sourceUserId={user?._id}
          sourceFamilyId={user?.familyId}
          onSuccess={onMigrateSuccess}
        />
      )}
      {mergeWizardPersons && (
        <MergePersonWizard
          open={!!mergeWizardPersons}
          retained={mergeWizardPersons.retained}
          deleted={mergeWizardPersons.deleted}
          onClose={() => setMergeWizardPersons(null)}
          onSuccess={() => {
            const deletedIdentifier =
              mergeWizardPersons?.deleted?.familyMemberId;
            const deletedMember = deletedIdentifier
              ? memberRecords.find(
                  (m) =>
                    m.familyMemberId === deletedIdentifier ||
                    m.proBandId === deletedIdentifier
                )
              : null;
            if (deletedMember) {
              const deletedPid = deletedMember.pid;
              setMemberRecords((prev) =>
                prev
                  .filter((p) => p.pid !== deletedPid)
                  .map((p) => stripMemberReferencesToPid(p, deletedPid))
              );
            }
            setMergeWizardPersons(null);
            queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });
            queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
            queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
            window.dispatchEvent(new CustomEvent('clinical-notes-refresh'));
            axios
              .get(
                APICONSTANTS.getPedigreeDetails.replace(
                  ':familyId',
                  user?.familyId
                )
              )
              .then(({ data }) => {
                dispatchUpdate({
                  type: EVENTS.SET_PEDIGREE_SELECTED_USER,
                  value: {
                    family: data.families,
                    selectedUser: data.proband,
                    perf: data.perf
                  }
                });
              })
              .catch(() => {});
          }}
        />
      )}
    </Box>
  );
});

export default ChartBox;
